import ListView from '@/components/datagrid/ListView';
import ArticleItems from '@/components/forms/articleItems/ArticleItems';
import useTitle from '@/hooks/useTitle';
import proxy from '@/services/ProxyService';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';

import { Close, PrintOutlined } from '@mui/icons-material';
import createItemLabel from '@/services/utils/labels';
import useSearchStore from '@/store/search';

const printLabelFields = [
  { label: 'Zeile 1', key: 'l1Content' },
  { label: 'Zeile 2', key: 'l2Content' },
  { label: 'Zeile 3', key: 'l3Content' },
  { label: 'Zeile 4', key: 'l4Content' },
  { label: 'Zeile 5', key: 'l5Content' },
];

export default function Labels() {
  const { config } = useRouteLoaderData('auth');
  useTitle('Labels');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLabelPrintDialogOpen, setIsLabelPrintDialogOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      label: 'Labels',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });
    return () => unregisterLocalSearch();
  }, [registerLocalSearch, unregisterLocalSearch]);

  // API-Suche
  const activeRequest = useRef(0);
  const iface = config?.warehouse?.weclappInterface;

  const search = useMemo(
    () => async (str) => {
      const q = str.trim();
      if (!iface || !q) {
        setItems([]);
        return;
      }

      const reqId = ++activeRequest.current;
      setLoading(true);
      try {
        const [{ data: orderResults }, { data: shipmentResults }] =
          await Promise.all([
            proxy.get(
              `/${iface}/salesOrder?or-orderNumber-eq=${encodeURIComponent(
                q,
              )}&or-orderNumber-like=${encodeURIComponent(`AU-${q}`)}`,
            ),
            proxy.get(
              `/${iface}/shipment?shipmentNumber-eq=${encodeURIComponent(q)}`,
            ),
          ]);

        if (reqId === activeRequest.current) {
          const merged = [
            ...(orderResults?.result ?? []),
            ...(shipmentResults?.result ?? []),
          ].map((row) => ({ _id: row.id, ...row }));
          setItems(merged);
        }
      } catch (e) {
        setItems([]);
      } finally {
        if (reqId === activeRequest.current) setLoading(false);
      }
    },
    [iface],
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setItems([]);
      return;
    }
    const t = setTimeout(() => search(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, search]);

  const [formData, setFormData] = useState({
    l1Content: '',
    l2Content: '',
    l3Content: '',
    l4Content: '',
    l5Content: '',
  });

  const handleChange = (field) => (event) =>
    setFormData((s) => ({ ...s, [field]: event.target.value }));

  const handlePrint = () => {
    const pdf = createItemLabel(
      { l1Content: formData.l1Content },
      { l2Content: formData.l2Content },
      { l3Content: formData.l3Content },
      { l4Content: formData.l4Content },
      { l5Content: formData.l5Content },
    );
    const url = URL.createObjectURL(pdf);
    window.open(url);
  };

  const columns = [
    {
      field: 'type',
      headerName: 'Typ',
      width: 100,
      valueGetter: (_value, row) =>
        row?.orderNumber ? 'Auftrag' : 'Lieferung',
    },
    {
      field: 'number',
      headerName: 'Nummer',
      width: 200,
      valueGetter: (_value, row) => row?.orderNumber || row?.shipmentNumber,
    },
    {
      field: 'customerName',
      headerName: 'Kunde',
      flex: 1,
      valueGetter: (_value, row) => row?.invoiceAddress?.company,
    },
    {
      field: 'commission',
      headerName: 'Kommission',
      flex: 1,
    },
  ];

  const actions = [
    <Tooltip title="Eigenes Label drucken">
      <IconButton onClick={() => setIsLabelPrintDialogOpen(true)}>
        <PrintOutlined sx={{ fontSize: '24px' }} />
      </IconButton>

      <Dialog
        open={isLabelPrintDialogOpen}
        onClose={() => setIsLabelPrintDialogOpen(!isLabelPrintDialogOpen)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Print Labels</DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '10px !important',
          }}
        >
          {printLabelFields.map(({ label, key }) => (
            <TextField
              key={key}
              label={label}
              value={formData[key]}
              onChange={handleChange(key)}
            />
          ))}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '15px',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => setIsLabelPrintDialogOpen(!isLabelPrintDialogOpen)}
              variant="text"
              color="error"
              startIcon={<Close />}
            >
              Abbrechen
            </Button>

            <Button
              onClick={handlePrint}
              variant="contained"
              startIcon={<PrintOutlined />}
            >
              Drucken
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Tooltip>,
  ];

  return (
    <ListView
      key={searchQuery}
      rows={items}
      columns={columns}
      actions={actions}
      loading={loading}
      detailComponents={[{ Component: ArticleItems, title: 'Positionen' }]}
    />
  );
}
